import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PageForm from 'components/pageForm/PageForm';
import { apiErrorText } from 'helper/apiErrorText';
import { clientSizeLabel } from 'constants/clientSizes';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import AdminCallGoals from 'pages/pages/AdminCallGoals';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

const THRESHOLDS = ['grand_compte', 'pme'];
// As the api takes them (settings/client-sizes/update): whole numbers of
// ETP, from 1 to 10 000.
const MAX_ETP = 10000;
const isEtp = (value) =>
  /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= MAX_ETP;
const draftOf = (thresholds) =>
  Object.fromEntries(THRESHOLDS.map((key) => [key, String(thresholds[key])]));

/**
 * « Paramètres · Taille des clients » of the admin console: the ETP from which
 * a client is a key account, then an SME (below, a very small business), as
 * in the « Tableau pilotage ». Read and saved by the api
 * (admin/crm/settings/client-sizes); a client with running missions takes its
 * size from them, any other company keeps the one chosen by hand. Below,
 * the daily call goals of each person who prospects.
 */
const AdminClientSizes = () => {
  const { text } = useText('adminConsole');
  const { language } = UseContext();
  const { call, ready } = useCrmContext();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    call(crmApi.getClientSizeSettings).then(
      (thresholds) => {
        setSaved(thresholds);
        setDraft(draftOf(thresholds));
      },
      () => setFailed(true),
    );
  }, [call]);
  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  const changed =
    saved &&
    draft &&
    THRESHOLDS.some((key) => draft[key] !== String(saved[key]));
  let problem = null;
  if (draft && !THRESHOLDS.every((key) => isEtp(draft[key])))
    problem = text('sizesInvalid');
  else if (draft && Number(draft.pme) >= Number(draft.grand_compte))
    problem = text('sizesOrder');

  const save = () => {
    setSaving(true);
    call(crmApi.saveClientSizeSettings, {
      grand_compte: Number(draft.grand_compte),
      pme: Number(draft.pme),
    }).then(
      // The values shown are the api's, as it kept them.
      (thresholds) => {
        setSaving(false);
        setSaved(thresholds);
        setDraft(draftOf(thresholds));
        toast.success(text('sizesSaved'));
      },
      (error) => {
        setSaving(false);
        toast.error(apiErrorText(error, { language }));
      },
    );
  };

  const field = (key) => (
    <span className="flex items-center gap-2">
      {text('sizesFrom')}
      <input
        type="text"
        inputMode="numeric"
        aria-label={text('sizesInputLabel', {
          size: clientSizeLabel(language, key),
        })}
        value={draft[key]}
        onChange={(event) =>
          setDraft((current) => ({ ...current, [key]: event.target.value }))
        }
        className={`h-9 w-20 rounded-md border bg-white px-3 text-right text-sm text-gray-900 focus:outline-none ${isEtp(draft[key]) ? 'border-gray-200 focus:border-yellow-500' : 'border-red-500'}`}
      />
      {text('sizesEtp')}
    </span>
  );

  return (
    <PageForm title={text('sizesPageTitle')}>
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 px-4 py-6 lg:px-8">
        {failed && (
          <CompactEmptyState
            title={text('sizesLoadError')}
            action={{ label: text('goalsRetry'), onClick: load }}
          />
        )}
        {!failed && !draft && (
          <div
            className="h-40 animate-pulse rounded-lg bg-gray-100"
            aria-busy="true"
          />
        )}
        {!failed && draft && (
          <section className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white">
            <div className="flex flex-col gap-0.5 px-4 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                {text('sizesTitle')}
              </h2>
              <p className="text-sm text-gray-500">{text('sizesText')}</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-gray-100">
                  <th className="bg-gray-50 py-3.5 pl-4 pr-2 text-left font-medium text-gray-700">
                    {text('sizesSize')}
                  </th>
                  <th className="bg-gray-50 py-3.5 pl-2 pr-4 text-right font-medium text-gray-700">
                    {text('sizesRule')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {THRESHOLDS.map((key) => (
                  <tr key={key} className="border-b border-gray-50">
                    <td className="py-3 pl-4 pr-2 text-gray-900">
                      {clientSizeLabel(language, key)}
                    </td>
                    <td className="flex justify-end py-3 pl-2 pr-4 text-gray-600">
                      {field(key)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="py-3 pl-4 pr-2 text-gray-900">
                    {clientSizeLabel(language, 'tpe')}
                  </td>
                  <td className="py-3 pl-2 pr-4 text-right text-gray-600">
                    {text('sizesBelow', {
                      etp: isEtp(draft.pme) ? draft.pme : String(saved.pme),
                    })}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
              {text('sizesHow')}
            </p>
            {changed && (
              <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-4 py-3">
                <span className="mr-auto text-xs text-gray-500">
                  {problem ?? text('goalsUnsaved')}
                </span>
                <Button
                  color="SECONDARY"
                  size="SM"
                  onClick={() => setDraft(draftOf(saved))}
                >
                  {text('goalsCancel')}
                </Button>
                <Button
                  color="PRIMARY"
                  size="SM"
                  disabled={Boolean(problem) || saving}
                  onClick={save}
                >
                  {text('goalsSave')}
                </Button>
              </div>
            )}
          </section>
        )}
        <AdminCallGoals />
      </div>
    </PageForm>
  );
};

export default AdminClientSizes;
